import React, { useState, useEffect } from 'react';
import { Button, Popover, Select, Divider, Tag } from 'antd';
import { FilterOutlined, PlusOutlined, DeleteOutlined, CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { FiltersProps, SelectedFilter } from '../../types/filters.types';
import { formatString } from '../../utils/formatString.util';

const Filters: React.FC<FiltersProps> = ({
  availableFilters,
  onFilterChange,
  className = '',
  buttonText,
  initialFilters,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilter[]>(
    initialFilters || [{ key: '', values: [] }]
  );

  useEffect(() => {
    if (initialFilters) {
      setSelectedFilters(initialFilters);
    }
  }, [initialFilters]);

  const handleVisibleChange = (newVisible: boolean) => {
    setVisible(newVisible);
  };

  const handleFilterKeyChange = (value: string, index: number) => {
    const newFilters = [...selectedFilters];
    newFilters[index] = { ...newFilters[index], key: value, values: [] };
    setSelectedFilters(newFilters);
    notifyFilterChange(newFilters);
  };

  const handleFilterValueChange = (values: any[], index: number) => {
    const newFilters = [...selectedFilters];
    newFilters[index] = { ...newFilters[index], values };
    setSelectedFilters(newFilters);
    notifyFilterChange(newFilters);
  };

  const notifyFilterChange = (filters: SelectedFilter[]) => {
    const validFilters = filters.filter((filter) => filter.key && filter.values.length > 0);
    onFilterChange(validFilters);
  };

  const handleAddMoreFilters = () => {
    setSelectedFilters([...selectedFilters, { key: '', values: [] }]);
  };

  const handleRemoveFilter = (index: number) => {
    const newFilters = selectedFilters.filter((_, i) => i !== index);
    if (newFilters.length === 0) {
      newFilters.push({ key: '', values: [] });
    }
    setSelectedFilters(newFilters);
    notifyFilterChange(newFilters);
  };

  const handleRemoveFilterValue = (filterKey: string, valueToRemove: any) => {
    const newFilters = [...selectedFilters];
    const filterIndex = newFilters.findIndex((filter) => filter.key === filterKey);

    if (filterIndex !== -1) {
      const filter = newFilters[filterIndex];
      const newValues = filter.values.filter((value) => value !== valueToRemove);

      if (newValues.length === 0) {
        // If no values left, remove the entire filter
        newFilters.splice(filterIndex, 1);
        if (newFilters.length === 0) {
          newFilters.push({ key: '', values: [] });
        }
      } else {
        // Update with remaining values
        newFilters[filterIndex] = { ...filter, values: newValues };
      }

      setSelectedFilters(newFilters);
      notifyFilterChange(newFilters);
    }
  };

  const getFilterValues = (filterKey: string) => {
    const filter = availableFilters.find((f) => f.filterKey === filterKey);
    if (!filter) return [];

    // Support both string values and objects with {label, value}
    return filter.filterValues.map((item) => {
      if (typeof item === 'object' && item !== null && 'label' in item && 'value' in item) {
        return item;
      }
      return { label: item, value: item };
    });
  };

  // Get the display label for a filter value
  const getFilterValueLabel = (filterKey: string, value: any): string => {
    const filterOptions = getFilterValues(filterKey);
    const option = filterOptions.find((opt) => opt.value === value);
    return option?.label ?? value;
  };

  // Extract the last part of a filter key (after the last dot) and format it
  // e.g., "ticketManagement.rootCauseCategory" -> "Root Cause Category"
  const getFilterLabel = (filterKey: string): string => {
    if (!filterKey) return '';
    const parts = filterKey.split('.');
    const label = parts.length > 1 ? parts[parts.length - 1] : filterKey;
    return formatString(label);
  };

  const getAvailableFilterOptions = () => {
    return availableFilters.map((f) => ({
      label: getFilterLabel(f.filterKey),
      value: f.filterKey,
    }));
  };

  const content = (
    <div style={{ minWidth: '500px', maxWidth: '600px' }}>
      {selectedFilters.map((filter, index) => (
        <div key={index} className="row mb-3 align-items-center">
          <div className="col-5">
            <Select
              placeholder={t('common.selectFilter')}
              style={{ width: '100%' }}
              value={filter.key || undefined}
              onChange={(value) => handleFilterKeyChange(value, index)}
              options={getAvailableFilterOptions()}
              disabled={disabled}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              allowClear
            />
          </div>
          <div className="col-6">
            <Select
              mode="multiple"
              placeholder={t('common.selectValues')}
              style={{ width: '100%' }}
              value={filter.values}
              onChange={(values) => handleFilterValueChange(values, index)}
              options={getFilterValues(filter.key)}
              disabled={!filter.key || disabled}
              maxTagCount={2}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </div>
          <div className="col-1 text-center">
            {selectedFilters.length > 1 && (
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleRemoveFilter(index)}
                size="small"
                disabled={disabled}
              />
            )}
          </div>
        </div>
      ))}
      <Divider style={{ margin: '12px 0' }} />
      <div className="text-center">
        <Button
          type="dashed"
          onClick={handleAddMoreFilters}
          icon={<PlusOutlined />}
          size="small"
          disabled={disabled}
        >
          {t('crud.create.addMore')}
        </Button>
      </div>
    </div>
  );

  // Count active filters
  const activeFilterCount = selectedFilters.filter((f) => f.key && f.values.length > 0).length;

  // Get filters that are actually active for displaying as pills
  const activeFilters = selectedFilters.filter((f) => f.key && f.values.length > 0);

  return (
    <div className={className}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '8px',
        }}
      >
        <Popover
          content={content}
          title={t('common.filters')}
          trigger="click"
          open={visible}
          onOpenChange={handleVisibleChange}
          placement="bottomRight"
          arrow={true}
        >
          <Button
            icon={<FilterOutlined />}
            type={activeFilterCount > 0 ? 'primary' : 'default'}
            disabled={disabled}
          >
            {buttonText || t('common.filters')}
          </Button>
        </Popover>

        {activeFilters.map((filter) => (
          <React.Fragment key={filter.key}>
            {filter.values.map((value) => (
              <Tag
                key={`${filter.key}-${value}`}
                closable
                color="default"
                onClose={() => handleRemoveFilterValue(filter.key, value)}
                style={{ margin: 0 }}
              >
                {getFilterLabel(filter.key)}: {getFilterValueLabel(filter.key, value)}
              </Tag>
            ))}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default Filters;
